// Venddi™ POS — Data definitions (refactored out of VenddiSoftwarePage.js)
// Accent color replaces legacy yellow with a sky-blue derivative.
import {
  ShoppingCart, Zap, Brain, ShieldCheck, CreditCard, Cpu, Sparkles, MessageCircle, CheckCircle,
  Database, Wifi, RefreshCw, Eye, Bell, Gauge, Activity, Bot, Building2, Receipt, TrendingUp, Users,
  Clock, Package, AlertTriangle, Send, Settings, Heart, Trophy, Monitor, Layers, FileText, BarChart3,
  Rocket, Lock, Flame, Banknote, GitBranch, Target, Layout,
} from 'lucide-react';

// ---- Brand palette (accent replaces yellow) ----
export const VENDDI_COLORS = {
  navy: '#0A2540',
  deep: '#050E1F',
  primary: '#0EA5E9', // sky-500
  accent: '#38BDF8',  // sky-400 — replaces former #F8D90F
  accentSoft: 'rgba(56, 189, 248, 0.12)',
  accentBorder: 'rgba(56, 189, 248, 0.30)',
  teal: '#0191B4',
};

export const PLANES = [
  {
    key: 'starter',
    name: 'Starter',
    price: '1',
    unit: 'UF',
    period: '/mes por kiosco',
    subtitle: 'Kiosco individual — tiendas de barrio, farmacias, food kiosks',
    tx: 'Hasta 800 transacciones/mes',
    features: [
      'Licencia Venddi™ POS para 1 kiosco',
      'Integración SII (boleta + factura electrónica)',
      'Transbank o Getnet incluido',
      'Catálogo ilimitado de productos',
      'Dashboard básico con ventas del día',
      'Soporte por email 12/5',
    ],
    ideal: 'Kioscos con bajo volumen, single-store',
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '2',
    unit: 'UF',
    period: '/mes por kiosco',
    subtitle: 'Para retailers que quieren escalar con IA supervisando',
    tx: 'Hasta 5.000 transacciones/mes',
    featured: true,
    features: [
      'Todo lo de Starter +',
      'Conector nativo a tu ERP (SAP, Defontana, Manager, Laudus, Nubox, Softland)',
      'Transbank + Getnet + SumUp (multi-acquirer)',
      'Kian™ supervisando operación 24/7',
      'Dashboard tiempo real multi-kiosco',
      'Alertas automáticas a WhatsApp',
      'Reportería SII automatizada',
      'Fidelización básica (puntos + cupones)',
      'Soporte 24/7 chat + WhatsApp',
    ],
    ideal: 'Cadenas con 5-50 kioscos, supermercados medianos',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: '3',
    unit: 'UF',
    period: '/mes por kiosco',
    subtitle: 'Para operaciones masivas, aeropuertos y retailers tier 1',
    tx: 'Transacciones ilimitadas',
    features: [
      'Todo lo de Pro +',
      'Integración custom con cualquier ERP/WMS',
      'Multi-moneda y multi-país',
      'IA predictiva de demanda (Kian forecast)',
      'Detección de fraude en tiempo real',
      'Cross-sell / upsell automático por IA',
      'Dashboard C-Suite con KPIs ejecutivos',
      'SLA 99.95% con respaldo financiero',
      'Ingeniero dedicado de cuenta',
      'API completa + webhooks',
    ],
    ideal: 'Retailers tier 1, aeropuertos, cadenas +50 kioscos',
  },
];

export const WOW_CONCEPTS = [
  {
    num: '01',
    title: 'Modo Pánico™',
    icon: AlertTriangle,
    color: '#EF4444',
    subtitle: 'Si se cae internet, Venddi sigue vendiendo',
    desc: 'Cuando detecta pérdida de conexión, Venddi entra en "Modo Pánico" y opera 100% offline. Guarda todas las transacciones localmente y las sincroniza automáticamente con el SII y tu ERP cuando vuelve la red. Tu caja nunca se detiene.',
    metric: '0 min',
    metricLabel: 'downtime de ventas',
  },
  {
    num: '02',
    title: 'AutoTune Menu™',
    icon: Brain,
    color: '#0EA5E9',
    subtitle: 'El menú se reordena solo según qué vende más',
    desc: 'Kian aprende qué productos convierten mejor en cada hora y día. Automáticamente reacomoda el orden de visualización del catálogo para poner lo más vendible arriba. Hace A/B testing continuo sin intervención humana.',
    metric: '+18%',
    metricLabel: 'ticket promedio',
  },
  {
    num: '03',
    title: 'Queue Predictor™',
    icon: Activity,
    color: '#8B5CF6',
    subtitle: 'Sabe cuándo vas a saturarte antes que tú',
    desc: 'Con machine learning sobre histórico y contexto (día, clima, eventos), Venddi predice picos de demanda con 30-60 min de anticipación y alerta a gerencia por WhatsApp. Puedes abrir más cajas o llamar refuerzos antes del colapso.',
    metric: '87%',
    metricLabel: 'precisión predicción',
  },
  {
    num: '04',
    title: 'Voice-to-Buy™',
    icon: Sparkles,
    color: VENDDI_COLORS.accent,
    subtitle: 'Comprar hablándole al kiosco (Beta 2026)',
    desc: 'Integración con Whisper + Claude para que clientes mayores o con movilidad reducida puedan decir "quiero un café grande con leche de almendras" y el kiosco arma el pedido solo. Accesibilidad real, no slogan.',
    metric: '+24%',
    metricLabel: 'conversión seniors',
  },
  {
    num: '05',
    title: 'Smart Upsell™',
    icon: Target,
    color: '#10B981',
    subtitle: 'Vende más sin que el cliente lo note',
    desc: 'Kian analiza el carrito en tiempo real y sugiere el combo, extra o adicional de mayor margen — al precio que maximiza la probabilidad de sí. Cada sugerencia está personalizada por patrón histórico del cliente anónimo o conocido.',
    metric: '+32%',
    metricLabel: 'margen por ticket',
  },
];

export const CARACTERISTICAS = [
  { icon: Receipt, title: 'Boleta y factura SII automática', desc: 'Timbre electrónico directo en 30 segundos. Sin middleware externo.' },
  { icon: CreditCard, title: 'Multi-acquirer', desc: 'Transbank, Getnet, SumUp. Switch automático si uno falla.' },
  { icon: Database, title: '20+ ERPs conectados', desc: 'SAP, Oracle, Defontana, Manager, Laudus, Nubox, Softland, Flexline, Odoo y más.' },
  { icon: Wifi, title: 'Offline-first', desc: 'Opera sin internet. Sincroniza cuando vuelve la conexión.' },
  { icon: Layout, title: 'UI personalizable', desc: 'Tu logo, colores, fondos. Templates por industria listos.' },
  { icon: Package, title: 'Catálogo ilimitado', desc: 'Miles de SKUs, variantes, combos y promociones dinámicas.' },
  { icon: Bot, title: 'Kian™ orquestador', desc: 'Supervisa cada transacción, alerta anomalías, optimiza flujo.' },
  { icon: BarChart3, title: 'Analytics en vivo', desc: 'KPIs por kiosco, hora, producto. Exportable y API-friendly.' },
  { icon: Lock, title: 'PCI DSS ready', desc: 'Pagos certificados. Datos encriptados end-to-end.' },
  { icon: Flame, title: 'Performance extrema', desc: 'Tiempo de respuesta <150ms. Arranque en 3 seg.' },
  { icon: GitBranch, title: 'Actualizaciones OTA', desc: 'Deploy de features sin bajar los kioscos. Cero intervención en terreno.' },
  { icon: Gauge, title: 'SLA 99.95% Enterprise', desc: 'Respaldo contractual. Créditos por incumplimiento.' },
];

export const BENEFICIOS = [
  { icon: TrendingUp, metric: '+23%', label: 'Ticket promedio', desc: 'Venta cruzada + smart upsell automatizado' },
  { icon: Clock, metric: '-70%', label: 'Tiempo de atención', desc: 'Vs. caja tradicional, según benchmarks retail' },
  { icon: Users, metric: '-60%', label: 'Costo en horas hombre', desc: '1 operador supervisa 6-8 kioscos simultáneos' },
  { icon: Target, metric: '0', label: 'Errores de tipeo', desc: 'Cliente elige directo, sin intermediarios' },
  { icon: Heart, metric: '+34', label: 'Puntos NPS', desc: 'Menor fricción, menos colas, más autonomía' },
  { icon: Receipt, metric: '100%', label: 'Cumplimiento SII', desc: 'Timbrado electrónico automático, sin contabilidad manual' },
];

export const ERP_LOGOS = [
  'SAP', 'Oracle NetSuite', 'Defontana', 'Manager.io', 'Laudus', 'Nubox',
  'Softland', 'Flexline', 'Odoo', 'Microsoft Dynamics', 'SAP B1', 'Totvs',
];

export const APLICACIONES = [
  { icon: ShoppingCart, title: 'Retail / Supermercados', desc: 'Self-checkout, express lanes, cajas de apoyo en peak', color: '#0EA5E9' },
  { icon: Building2, title: 'Food Service / QSR', desc: 'Kioscos de pedido en McDonald\'s, Burger King, KFC style', color: '#F97316' },
  { icon: Rocket, title: 'Aeropuertos', desc: 'Food court, duty free, carga de tarjetas, check-in asistido', color: '#8B5CF6' },
  { icon: Heart, title: 'Salud / Farmacias', desc: 'Dispensación de OTC, pedido de bonos, pago isapre', color: '#10B981' },
  { icon: Monitor, title: 'Cines y entretenimiento', desc: 'Compra de entradas, combos, productos combinados', color: '#EF4444' },
  { icon: Layers, title: 'Retail general', desc: 'Ferreterías, librerías, deportes. Self-service con catálogo amplio', color: '#0191B4' },
];

export const FAQS = [
  { q: '¿Cómo se cuenta una "transacción" para efectos del plan?', a: 'Cada boleta o factura emitida al SII cuenta como 1 transacción, sin importar cuántos items tenga. Devoluciones no cuentan. Si superas el límite de tu plan en un mes, facturamos el exceso a prorrata del plan superior sin forzarte a upgradear.' },
  { q: '¿Qué pasa si quiero cambiar de ERP?', a: 'Todos los conectores son parte del servicio. Si migras de Defontana a SAP, hacemos la reconexión sin costo de re-implementación. Solo cambia el archivo de configuración.' },
  { q: '¿Cómo funciona Kian™ en la operación?', a: 'Kian es una capa de IA que monitorea en tiempo real cada transacción, detecta anomalías (tickets atípicos, demoras, fallos de pago), orquesta el menú según conversión y alerta a gerencia por WhatsApp. No reemplaza a tu equipo — lo potencia.' },
  { q: '¿Puedo empezar con 1 kiosco y escalar?', a: 'Sí. El plan Starter es justamente para eso. Cuando escales, te migramos al Pro sin fricción. No hay fees de upgrade.' },
  { q: '¿Qué incluye el SLA 99.95% del plan Enterprise?', a: 'Uptime mensual garantizado con respaldo contractual. Si no se cumple, te acreditamos 5-15% de la facturación del mes siguiente según la severidad.' },
  { q: '¿Soporta Transbank POS Integrado o sólo Smartpos?', a: 'Ambos. Soportamos POS Integrado (ESPI/QR/Contactless), Transbank Smartpos vía Bluetooth y el portal NET. Para Getnet, conexión directa vía API oficial.' },
  { q: '¿Qué hardware recomiendan?', a: 'Venddi corre sobre nuestros kioscos Wintec y iMin, pero es hardware-agnóstico. Funciona en cualquier Windows 10/11 IoT o Android 11+ con pantalla táctil ≥15".' },
  { q: '¿Cuánto demora la implementación?', a: '5-7 días hábiles para el plan Pro (incluye integración ERP, timbrado SII, medios de pago y capacitación). Enterprise puede tomar 15-25 días según la complejidad del ERP legacy.' },
];

export const HERO_STATS = [
  { v: 23, suffix: '%', prefix: '+', label: 'Ticket promedio proyectado' },
  { v: 70, suffix: '%', prefix: '−', label: 'Menos horas hombre' },
  { v: 99.9, suffix: '%', prefix: '', label: 'SLA Enterprise', decimals: 1 },
];

export const KIAN_FEED_EVENTS = [
  { t: '2 min', txt: 'Pico de demanda detectado en Kiosco 12 · Notifiqué a supervisor', c: VENDDI_COLORS.accent, icon: TrendingUp },
  { t: '5 min', txt: 'Stock bajo: Croissant almendras (Kiosco 3). Pedí reposición.', c: '#EF4444', icon: AlertTriangle },
  { t: '8 min', txt: 'Upsell exitoso en Kiosco 7: +$1.990 (ticket promedio +18%)', c: '#10B981', icon: Target },
  { t: '14 min', txt: 'Transbank lento (2.3s). Switch automático a Getnet.', c: '#0EA5E9', icon: CreditCard },
  { t: '22 min', txt: 'Menú re-optimizado: cambio orden top 10 productos', c: '#8B5CF6', icon: Brain },
];

export const PAIN_POINTS = [
  { icon: Clock, title: 'Cada cliente espera 4-6 min', desc: 'En peak se pierden ventas porque la gente se va de la cola.' },
  { icon: Users, title: '1 cajero = 1 cliente', desc: 'Relación directa entre personal y throughput. Escalar es caro.' },
  { icon: AlertTriangle, title: '2-5% errores de tipeo', desc: 'Cobros mal, devoluciones, ajustes manuales de inventario.' },
];
